import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { identityClient } from '../sdk/identityClient.js';
import { clearSession, hasSession } from '../sdk/sessionTokenStorage.js';
import { brokeredLogin } from '../sdk/brokeredLogin.js';
import { identityServerClient } from '../sdk/identityServerClient.js';
import { getCachedUser, setCachedUser } from '../sdk/cachedUserStorage.js';

/**
 * Who is signed in, and everything that changes it.
 *
 * The first store to move here, and deliberately: it is small, it is
 * load-bearing on the boot path, and it is the one where a second surface
 * building its own had already gone wrong in a way anybody can check (the
 * destroyed desktop portal's parallel auth store — divergent login response
 * reads, localStorage-only hydration, local-only logout — forced the server
 * into serving dual projections of one record). This store hydrates from
 * GET /session/me and branches 401-vs-404 to tell "signed out" from "outage".
 *
 * What did NOT come with it is `homePathForRole()`, which used to be exported
 * from this file. Where a signed-in user lands is a fact about a surface's
 * routes, and this package has no routes.
 */

export const sessionStore = defineStore('sessionStore', () => {
  const user = ref(null);
  const email = ref(null);
  const loading = ref(false);
  const error = ref(null);

  const isAuthenticated = computed(() => !!user.value);
  // Dealer-only app: everyone who's signed in is a dealer. Kept as a computed
  // so the many dealer-gated UI bits keep reading `session.isDealer`.
  const isDealer = computed(() => isAuthenticated.value);

  function applyUser(nextUser) {
    user.value = nextUser ?? null;
    email.value = nextUser?.primaryEmail ?? null;
    // Keep the boot-paint cache in step (ruled: cache-with-revalidate). Fire
    // and forget — the cache is display-only convenience, never truth.
    void setCachedUser(nextUser ?? null);
  }

  // Paints the last hydrated user from the storage seam so boot renders
  // instantly while the real hydrate revalidates. Display only: it never
  // makes anyone authenticated for a decision the server wouldn't back.
  async function paintFromCache() {
    if (user.value) return;
    const cached = await getCachedUser();
    if (cached && !user.value) {
      user.value = cached;
      email.value = cached.primaryEmail ?? null;
    }
  }

  // Merge a partial patch onto the current user reactively. Used after a
  // successful PATCH /session/me round-trip so the drawer + bound pages
  // reflect the new value without waiting on a full re-hydrate. Nested
  // notificationPrefs are merged shallowly so flipping one toggle doesn't
  // wipe the rest of the map.
  function applyUserPatch(patch) {
    if (!user.value || !patch) return;
    const next = { ...user.value, ...patch };
    if (patch.notificationPrefs) {
      next.notificationPrefs = {
        ...(user.value.notificationPrefs ?? {}),
        ...patch.notificationPrefs
      };
    }
    user.value = next;
  }

  // Whitelist-driven update. The server validates the same whitelist and
  // returns the full updated user record, which we apply via applyUser so
  // any server-side coercions (e.g. trimmed strings) land locally too.
  async function updateMe(patch) {
    const updated = await identityClient.updateMe(patch);
    if (updated && typeof updated === 'object') {
      applyUser(updated);
    } else {
      // Defensive — if the server ever returns 204 instead of the doc,
      // refresh from /me to keep local state in sync.
      const me = await identityClient.getMe();
      applyUser(me);
    }
    return user.value;
  }

  // Sign out this device. Terminal: ends in a full-page navigation to the
  // hosted logout. Callers must not navigate afterward — a later assign
  // supersedes it.
  async function logout() {
    try {
      // Spends this device's refresh token, forgets its key, and closes its
      // live connections.
      await identityServerClient.revokeSession();
    } catch {
      // Ignore errors so a network blip can't trap the user in a logged-in
      // state locally; the session it could not revoke still ends at its cap.
    }
    await leave();
  }

  // Sign out everywhere: every session the person has, on every device. Unlike
  // logout, a failure is thrown and nothing is cleared — the person stays
  // signed in here and is told, rather than believing the other devices are
  // signed out. On success it is terminal, like logout.
  async function logoutEverywhere() {
    await identityServerClient.revokeEverywhere();
    await leave();
  }

  async function leave() {
    await clearSession();
    applyUser(null);
    // End the hosted login's own session too — without this, the on-load
    // login redirect signs the user straight back in silently.
    brokeredLogin.beginLogout();
  }

  // Resolves the persisted session against the server. On success applies
  // the fresh user record; on 401 clears local state so the auth guard
  // bounces to /login on the next navigation.
  async function hydrate() {
    if (!(await hasSession())) {
      // No session — including whatever the cache painted.
      applyUser(null);
      return;
    }
    try {
      const me = await identityClient.getMe();
      applyUser(me);
    } catch (e) {
      if (e?.statusCode === 401 || e?.statusCode === 404) {
        await clearSession();
        applyUser(null);
        return;
      }
      // Don't wipe the local session on a transient network failure —
      // just leave hydration empty so the guard sends the user to /login.
      console.warn('session.hydrate failed', e);
    }
  }

  return {
    user,
    email,
    loading,
    error,
    isAuthenticated,
    isDealer,
    logout,
    logoutEverywhere,
    hydrate,
    paintFromCache,
    applyUserPatch,
    updateMe
  };
});
